// Phase 9A.1 — usePublicSignalCards hook (updated for Phase 9A schema).
//
// Read-only consumer of `signal_cards` rows. The Phase 9A migration
// replaced the old signal_cards table with a probability-intelligence
// schema. This hook queries the new columns and maps them to the
// existing PublicSignalCardViewModel interface so downstream Exchange
// components continue to work without changes.
//
// Trust contract:
//   - No external HTTP. No provider API calls. No LLM.
//   - Defensive parsing — missing fields render with fallbacks.
//   - Truthful empty state when there are no signal cards.

import { useEffect, useMemo, useState } from "react";

import { getBrowserClient } from "../supabase/browser.ts";

// Public-safe projection. Maps the Phase 9A signal_cards columns to
// the Exchange view-model that downstream components consume.
export interface PublicSignalCardViewModel {
  id: string;
  fixture_id: string;
  canonical_code: string;
  signal_label: string | null;
  value_label: string | null;
  risk_label: string | null;
  confidence_label: string | null;
  fair_probability: number | null;
  fair_odds_decimal: number | null;
  market_odds_decimal: number | null;
  market_implied_probability: number | null;
  edge_probability: number | null;
  value_edge: number | null;
  candidate_rank: number | null;
  data_tier: number | null;
  summary: string | null;
  caution_flags: readonly string[];
  generated_at: string;
  expires_at: string | null;
}

export interface PublicSignalCardsResult {
  cards: readonly PublicSignalCardViewModel[];
  loading: boolean;
  error: string | null;
}

export const PUBLIC_SIGNAL_CARDS_DEFAULT_LIMIT = 50;

function asNum(v: unknown): number | null {
  if (v === null || v === undefined) return null;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

function confidenceLabel(n: number | null): string | null {
  if (n === null) return null;
  if (n >= 80) return "high";
  if (n >= 50) return "medium";
  return "low";
}

function riskLabel(tradability: number | null): string | null {
  if (tradability === null) return null;
  if (tradability >= 0.7) return "low";
  if (tradability >= 0.4) return "medium";
  return "high";
}

function dataTier(tradability: number | null): number | null {
  if (tradability === null) return null;
  if (tradability >= 0.8) return 1;
  if (tradability >= 0.6) return 2;
  if (tradability >= 0.4) return 3;
  return 4;
}

function edgeLabel(edge: number | null): string | null {
  if (edge === null) return null;
  if (edge > 5) return "strong";
  if (edge > 0) return "positive";
  return "negative";
}

export function projectPublicSignalCard(row: Record<string, unknown>): PublicSignalCardViewModel {
  const agentProb = asNum(row.agent_probability);
  const impliedProb = asNum(row.implied_probability);
  const edgePct = asNum(row.edge_pct);
  const oddsDecimal = asNum(row.odds_decimal);
  const agentConfidence = asNum(row.agent_confidence);
  const tradabilityScore = asNum(row.tradability_score);

  return {
    id: String(row.id ?? ""),
    fixture_id: String(row.fixture_id ?? ""),
    canonical_code: String(row.market_type ?? "football_match_result"),
    signal_label: typeof row.title === "string" ? row.title : null,
    value_label: edgeLabel(edgePct),
    risk_label: riskLabel(tradabilityScore),
    confidence_label: confidenceLabel(agentConfidence),
    fair_probability: agentProb,
    fair_odds_decimal: oddsDecimal,
    market_odds_decimal: oddsDecimal,
    market_implied_probability: impliedProb,
    edge_probability: edgePct,
    value_edge: edgePct,
    candidate_rank: null,
    data_tier: dataTier(tradabilityScore),
    summary: typeof row.resolution_rule === "string" ? row.resolution_rule : null,
    caution_flags: [],
    generated_at: String(row.created_at ?? ""),
    expires_at: typeof row.resolves_at === "string" ? row.resolves_at : null,
  };
}

// PostgREST caps a single response (default max-rows ~1000). To "reveal
// all" — including a substrate-scale board of 1000+ cards — we page through
// with .range() until a short page comes back. PAGE_SIZE stays at/under the
// server cap; PAGE_HARD_STOP bounds the loop so a runaway can't hang the tab.
const PAGE_SIZE = 1000;
const PAGE_HARD_STOP = 50000;

type SignalCardsResp = {
  data: Record<string, unknown>[] | null;
  error: { message: string } | null;
};
interface SignalCardsQuery {
  select: () => {
    order: (col: string, opts: { ascending: boolean }) => {
      limit: (n: number) => Promise<SignalCardsResp>;
      range: (from: number, to: number) => Promise<SignalCardsResp>;
    };
  };
}

export function usePublicSignalCards(
  args: {
    /** Cap the fetch to N most-recent cards. Ignored when `all` is true. */
    limit?: number;
    /** Reveal the entire board — paginate past the server row cap. */
    all?: boolean;
  } = {},
): PublicSignalCardsResult {
  const all = args.all === true;
  const limit = args.limit ?? PUBLIC_SIGNAL_CARDS_DEFAULT_LIMIT;
  const [cards, setCards] = useState<readonly PublicSignalCardViewModel[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const client = getBrowserClient();
    setLoading(true);
    setError(null);
    void (async () => {
      // Cast through unknown since generated types haven't been regenerated yet.
      const table = client.from("signal_cards" as never) as unknown as SignalCardsQuery;

      let rows: Record<string, unknown>[] = [];
      let fetchError: string | null = null;

      if (all) {
        // Page through every row, newest-first, until a short page returns.
        for (let from = 0; from < PAGE_HARD_STOP; from += PAGE_SIZE) {
          const resp = await table
            .select()
            .order("created_at", { ascending: false })
            .range(from, from + PAGE_SIZE - 1);
          if (cancelled) return;
          if (resp.error) {
            fetchError = resp.error.message;
            break;
          }
          const batch = resp.data ?? [];
          rows = rows.concat(batch);
          if (batch.length < PAGE_SIZE) break;
        }
      } else {
        const resp = await table
          .select()
          .order("created_at", { ascending: false })
          .limit(limit);
        if (cancelled) return;
        if (resp.error) fetchError = resp.error.message;
        else rows = resp.data ?? [];
      }

      if (cancelled) return;
      if (fetchError) {
        setError(fetchError);
        setCards([]);
        setLoading(false);
        return;
      }

      // Filter to active signals only (Phase 9A equivalent of
      // visibility='public' AND signal_status='ready').
      const safeRows = rows.filter(
        (r) => r !== null && (r.status === "active" || r.status === "draft"),
      );
      setCards(safeRows.map(projectPublicSignalCard));
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [limit, all]);

  return useMemo(() => ({ cards, loading, error }), [cards, loading, error]);
}
